import { format } from "date-fns";
import intl from "react-intl-universal";
import {
  formatFilterAmount,
  formatFilterDate,
  parseAmountInput,
} from "./gridFilterUtils";

export const EMPTY_DATE_AMOUNT_SUB_ACCOUNT_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  subAccountId: "",
  searchQuery: "",
};

export const filtersFromApiFilterObj = (filterObj = {}) => ({
  dateFrom: filterObj.start_date
    ? new Date(filterObj.start_date.replace(/-/g, "/").replace(/T.+/, ""))
    : null,
  dateTo: filterObj.end_date
    ? new Date(filterObj.end_date.replace(/-/g, "/").replace(/T.+/, ""))
    : null,
  amountMin: filterObj.start_amount
    ? String(Number(filterObj.start_amount) / 100).replace(".", ",")
    : "",
  amountMax: filterObj.end_amount
    ? String(Number(filterObj.end_amount) / 100).replace(".", ",")
    : "",
  subAccountId: filterObj.sub_account_id || "",
  searchQuery: "",
});

export const apiFilterObjFromFilters = (filters) => ({
  start_date: filters.dateFrom ? format(filters.dateFrom, "yyyy-MM-dd") : "",
  end_date: filters.dateTo ? format(filters.dateTo, "yyyy-MM-dd") : "",
  start_amount: (() => {
    const parsed = parseAmountInput(filters.amountMin);
    return parsed === null ? "" : String(Math.round(parsed * 100));
  })(),
  end_amount: (() => {
    const parsed = parseAmountInput(filters.amountMax);
    return parsed === null ? "" : String(Math.round(parsed * 100));
  })(),
  sub_account_id: filters.subAccountId || "",
});

export const hasActiveDateAmountSubAccountFilters = (filters) =>
  Boolean(filters.dateFrom)
  || Boolean(filters.dateTo)
  || filters.amountMin !== ""
  || filters.amountMax !== ""
  || filters.subAccountId !== ""
  || Boolean(filters.searchQuery?.trim());

export const isDateAmountSubAccountChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return filters.amountMin !== "" || filters.amountMax !== "";
    case "subAccount":
      return filters.subAccountId !== "";
    case "more":
      return filters.subAccountId !== "";
    default:
      return false;
  }
};

export const buildDateAmountSubAccountFilterTags = (filters, subAccounts = []) => {
  const tags = [];

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom) {
    tags.push({
      key: "dateFrom",
      iconType: "date",
      value: formatFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    tags.push({
      key: "dateTo",
      iconType: "date",
      value: formatFilterDate(filters.dateTo),
    });
  }

  if (filters.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMin),
    });
  }

  if (filters.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMax),
    });
  }

  if (filters.subAccountId) {
    const subAccount = subAccounts.find((sa) => sa.subAccountId === filters.subAccountId);
    tags.push({
      key: "subAccountId",
      iconType: "subAccount",
      value: subAccount?.name || filters.subAccountId,
    });
  }

  return tags;
};

export const removeDateAmountSubAccountFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "dateFrom":
      return { ...filters, dateFrom: null };
    case "dateTo":
      return { ...filters, dateTo: null };
    case "amountMin":
      return { ...filters, amountMin: "" };
    case "amountMax":
      return { ...filters, amountMax: "" };
    case "subAccountId":
      return { ...filters, subAccountId: "" };
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    default:
      return filters;
  }
};

export const applyDateAmountSubAccountClientFilters = (items, filters) => {
  if (!filters) return items;

  const query = filters.searchQuery?.trim().toLowerCase() || "";
  const amountMin = filters.amountMin !== "" ? Number(String(filters.amountMin).replace(",", ".")) : null;
  const amountMax = filters.amountMax !== "" ? Number(String(filters.amountMax).replace(",", ".")) : null;
  const dateFrom = filters.dateFrom ? new Date(filters.dateFrom) : null;
  const dateTo = filters.dateTo ? new Date(filters.dateTo) : null;
  if (dateFrom) dateFrom.setHours(0, 0, 0, 0);
  if (dateTo) dateTo.setHours(23, 59, 59, 999);
  const subAccountId = filters.subAccountId || "";

  return items.filter((item) => {
    const created = item.created_at ? new Date(item.created_at) : null;
    if (dateFrom && created && created < dateFrom) return false;
    if (dateTo && created && created > dateTo) return false;

    const amountAbs = Math.abs(Number(item.amount) || 0) / 100;
    if (amountMin !== null && Number.isFinite(amountMin) && amountAbs < amountMin) return false;
    if (amountMax !== null && Number.isFinite(amountMax) && amountAbs > amountMax) return false;

    if (subAccountId) {
      const itemSub =
        item.sub_account_id || item.subAccountId || item.sub_account?.id || "";
      if (String(itemSub) !== String(subAccountId)) return false;
    }

    if (!query) return true;

    const haystack = [
      item.description,
      item.name,
      item.receiver?.name,
      item.sender?.name,
      item.operation_id,
      item.end_to_end_id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
};
